import "react-app-polyfill/ie11";
import React, { useState, useEffect, Suspense, lazy } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";

import ReactGA from "react-ga4";
import { v4 as uuidv4 } from "uuid";

import {
  BETA,
  Pages,
  // VALID_PATHS,
  CookieVersion,
  LS_COOKIE_SETTINGS,
  LS_GA_CLIENT_ID,
  LS_ONELINK,
  LS_EXPERIENCE,
  LS_ATTRIBUTION,
  PATHS_PENDING_ACCOUNT,
  SubscriptionOverdueGracePeriodSeconds,
  PlanFreeOnelinkLimit,
  PageDescriptor,
  PlanPrice,
} from "appConfig";
import { classNames, readToken } from "misc/helpers";
import { newOnelink } from "misc/onelink_utils";
import { apiLoadOnelinks } from "net/requests";
import { signInWithAccessToken } from "misc/auth_utils";

import { getLanguageString as _ } from "misc/lang";
import Loader from "components/util/Loader";
import { useLoader } from "misc/loader";
import verifyEmail from "components/auth/verify_email";
import { retrieveAccessToken, removeAccessToken } from "misc/auth_utils";

import css from "./app.module.scss";
import { Onelink } from "model/onelink";
import { User } from "model/user";
import { Account, AccountAttribution, LegacyAccountInfo } from "model/account";
import {
  AuthProps,
  DefaultProps,
  OnelinkProps,
  SubscriptionProps,
} from "model/props";
import { CookieSettings } from "model/cookies";
import { clearStats } from "misc/stats_utils";
import { getFaqMap, getFaqTagMap } from "misc/faq_util";

import CookieBanner from "components/cookiebanner/CookieBanner";
import Footer from "components/footer/Footer";
import TopMenu from "components/topmenu/TopMenu";
import LandingPage from "components/content/landingpage/LandingPage";
import SupportPage from "components/content/support/SupportPage";
import { getAttribution } from "misc/attribution";
import { hashCode } from "misc/utils";
import { formatTimestamp } from "misc/date_utils";

// LAZY LOADED COMPONENTS
const BetaWall = lazy(() => import("components/beta/BetaWall"));
const PricingPage = lazy(
  () => import("components/content/pricing/PricingPage")
);
const AboutPage = lazy(() => import("components/content/about/AboutPage"));
const MyOnelinks = lazy(() => import("components/onelink/MyOnelinks"));
const RegisterWrapper = lazy(() => import("components/auth/RegisterWrapper"));
const CompleteRegistration = lazy(
  () => import("components/account/CompleteRegistration")
);
const MyAccount = lazy(() => import("components/account/MyAccount"));
const ManageAccess = lazy(() => import("components/account/ManageAccess"));
const SetupPaymentWrapper = lazy(
  () => import("components/payment/SetupPaymentWrapper")
);
const ResetPassword = lazy(() => import("components/auth/ResetPassword"));
const SignInWrapper = lazy(() => import("components/auth/SignInWrapper"));
const UserSettings = lazy(() => import("components/auth/UserSettings"));
const Legal = lazy(() => import("components/legal/Legal"));
//--

var ls = require("local-storage");

const start_path = window.location.pathname;
const start_href = window.location.href;
const start_token = readToken();

const faqMap = getFaqMap();
const faqTagMap = getFaqTagMap();

// if (
//   !VALID_PATHS.includes(start_path.toLowerCase()) &&
//   !faqMap[start_path.substring(1).toLocaleLowerCase()]
// ) {
//   const redirectTo = "https://onelink.to" + start_path;
//   window.location.replace(redirectTo);
// }

const App: React.FC<undefined> = () => {
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false);

  const [betaUnlocked, setBetaUnlocked] = useState<boolean>(false);
  const [authProvider, setAuthProvider] = useState<string>();
  const [user, setUser] = useState<User>();
  const [account, setAccount] = useState<Account>();
  const [serverTime, setServerTime] = useState<number>();
  //KEEP const [localTime, setLocalTime] = useState<number>();
  const [legacyInfo, setLegacyInfo] = useState<LegacyAccountInfo>();
  const [onelinkList, _setOnelinkList] = useState<Onelink[]>();
  const [onelink, _setOnelink] = useState<Onelink>();
  const [passwordResetToken, setPasswordResetToken] = useState<string>();
  const [invitationToken, setInvitationToken] = useState<string>();
  const [loaderState, loader] = useLoader();
  const [cookies, _setCookies] = useState<CookieSettings>(
    ls.get(LS_COOKIE_SETTINGS) || {}
  );
  const [gaClientId, setGaClientId] = useState<string>();
  const [path, setPath] = useState<string>("/");

  const [lastPath, setLastPath] = useState<string>("/");
  const [lastTitle, setLastTitle] = useState<string>(document.title);
  const [navigateData, setNavigateData] = useState<any>();

  const [startupHref, setStartupHref] = useState<string>("");
  const [startupPath, setStartupPath] = useState<string>();
  const [attribution, setAttribution] = useState<AccountAttribution>({});

  ///////////////////////////////////////////////////////////////////////////

  const _navigate = useNavigate();

  ///////////////////////////////////////////////////////////////////////////

  const resetInvitationToken = () => {
    setInvitationToken(undefined);
  };

  ///////////////////////////////////////////////////////////////////////////

  const isLoggedIn = !!(user && account && serverTime);

  const haveActiveSubscription = !!(
    isLoggedIn &&
    account?.subscriptionSetup &&
    account?.subscriptionSetup !== "cancelled"
  );

  const isSubscriptionOverDue = !!(
    haveActiveSubscription && (account?.paidUntil || 0) < (serverTime || 0)
  );

  const isSubscriptionGracePeriodOverDue = !!(
    isSubscriptionOverDue &&
    (account?.paidUntil || 0) + SubscriptionOverdueGracePeriodSeconds <
    (serverTime || 0)
  );

  const linkCount = onelinkList?.length || 0;
  const legacyCount =
    onelinkList?.reduce((sum, o) => sum + (o.isLegacy ? 1 : 0), 0) || 0;
  const notLegacyCount = linkCount - legacyCount;

  const isCancelledAndOverFreeLimit = !!(
    isLoggedIn &&
    account?.subscriptionSetup === "cancelled" &&
    account?.currentPlan === "free" &&
    linkCount > PlanFreeOnelinkLimit &&
    notLegacyCount > 0
  );

  const currentPlanCancelled = !!(
    isLoggedIn &&
    account?.subscriptionSetup === "cancelled" &&
    account?.currentPlan !== "free"
  );

  // console.log("##################################");
  // console.log("active subscription: " + haveActiveSubscription);
  // console.log("sub: " + account?.subscriptionSetup);
  // console.log("until: " + formatTimestamp(account?.paidUntil || 0));
  // console.log("overdue: " + isSubscriptionOverDue);
  // console.log("overdue grace: " + isSubscriptionGracePeriodOverDue);
  // console.log("cancelled over free: " + isCancelledAndOverFreeLimit);
  // console.log("curr plan cancelled: " + currentPlanCancelled);
  // console.log("##################################");

  ///////////////////////////////////////////////////////////////////////////

  const setCookies = (cookies: CookieSettings) => {
    cookies.date = new Date().getTime();
    cookies.version = CookieVersion;

    _setCookies(cookies);
    ls.set(LS_COOKIE_SETTINGS, cookies);

    if (!cookies.experience) {
      clearExperienceCookies();
    }

    if (!cookies.performance) {
      clearStats();
    }

    if (cookies.analytics) {
      ls.set(LS_GA_CLIENT_ID, gaClientId);
      const attribution: AccountAttribution = getAttribution(startupHref, true);
      ls.set(LS_ATTRIBUTION, JSON.stringify(attribution));
    } else {
      ls.remove(LS_GA_CLIENT_ID);
      ls.remove(LS_ATTRIBUTION);
      gaSendConsent();
    }
  };

  const manageCookies = () => {
    const newCookies = { ...cookies, show: !cookies.show };
    _setCookies(newCookies);
  };

  const clearExperienceCookies = () => {
    for (const key of LS_EXPERIENCE) {
      ls.remove(key);
    }
  };

  ///////////////////////////////////////////////////////////////////////////

  const replaceOnelink = (onelink: Onelink) => {
    if (onelinkList) {
      const indexInList = onelinkList.findIndex(
        (ol: Onelink) => ol.onelinkId === onelink.onelinkId
      );

      if (indexInList >= 0) {
        onelinkList[indexInList] = onelink;
        _setOnelinkList(onelinkList);
      }
    }
  };

  const setOnelink = (onelink?: Onelink) => {
    if (cookies.experience) {
      if (onelink) {
        ls.set(LS_ONELINK, onelink.onelinkId as string);
      } else {
        ls.remove(LS_ONELINK);
      }
    }
    _setOnelink(onelink);
  };

  const setOnelinkList = (list: Onelink[]) => {
    const selectedId = ls.get(LS_ONELINK);
    var selectedOnelink = undefined;

    for (const onelink of list) {
      if (onelink.onelinkId === selectedId) selectedOnelink = onelink;
      if (typeof onelink.data === "string") {
        if (onelink.data.length > 0) {
          onelink.data = JSON.parse(onelink.data);
        } else {
          onelink.data = {};
        }
      }
    }
    _setOnelinkList(list);

    if (!selectedOnelink && list.length) selectedOnelink = list[0];
    setOnelink(selectedOnelink);
  };

  ///////////////////////////////////////////////////////////////////////////

  const loadOnelinks = () => {
    if (!user) return;

    apiLoadOnelinks(user)
      .then((response) => {
        const list = response.data;
        setOnelinkList(list);
        if (!list.length) {
          newOnelink({
            user: user,
            onelinkList: onelinkList,
            setOnelinkList: setOnelinkList,
            setOnelink: setOnelink,
          });
        }
      })
      .catch((error) => {
        console.log(error.response?.data?.error);
      });
  };

  ///////////////////////////////////////////////////////////////////////////

  const routePendingAction = (pendingAction: string): boolean => {
    switch (pendingAction) {
      case "complete-registration":
        navigate(Pages.COMPLETE_REGISTRATION);
        return true;
      case "setup-subscription":
        navigate(Pages.SETUP_PAYMENT);
        return true;
      // case "check-payment-methods":
      //   setActiveView(Paths.SETUP_PAYMENT);
      //   return true;
      default:
        return false;
    }
  };

  ///////////////////////////////////////////////////////////////////////////

  const clearLocalStorageOnSignout = () => {
    const ls_cookie_settings = ls.get(LS_COOKIE_SETTINGS);
    const ls_ga_client_id = ls.get(LS_GA_CLIENT_ID);
    ls.clear();
    ls.set(LS_COOKIE_SETTINGS, ls_cookie_settings);
    ls.set(LS_GA_CLIENT_ID, ls_ga_client_id);
  };

  const onSignedOut = () => {
    setAuthProvider(undefined);
    setUser(undefined);
    setAccount(undefined);
    _setOnelinkList(undefined);
    _setOnelink(undefined);
    removeAccessToken();
    clearLocalStorageOnSignout();
    clearStats();
    navigate(Pages.LANDING_PAGE);
  };

  ///////////////////////////////////////////////////////////////////////////

  const navigate = (
    page: string | PageDescriptor,
    event?: any,
    title?: string,
    data?: any
  ): boolean => {
    event && event.preventDefault();

    setLastPath(window.location.pathname);
    setLastTitle(document.title);

    if (typeof page === "string") {
      if (title) document.title = "Onelink.to - " + title;
      _navigate(page as string);
    } else {
      const pd = page as PageDescriptor;
      _navigate(pd.path);
      if (pd.title) document.title = "Onelink.to - " + pd.title;
    }

    setNavigateData(data || undefined);

    return false;
  };

  ///////////////////////////////////////////////////////////////////////////

  const defaultProps: DefaultProps = {
    user,
    setUser,
    account,
    setAccount,
    legacyInfo,
    setLegacyInfo,
    routePendingAction,
    loader,
    cookies,
    setCookies,
    manageCookies,
    navigate,
    navigateData,
    isSmallScreen,
    onSignedOut,
  };

  const authProps: AuthProps = {
    authProvider,
    setAuthProvider,
    onSignedOut,
    invitationToken,
    resetInvitationToken,
    passwordResetToken,
    setServerTime,
    attribution
  };

  const onelinkProps: OnelinkProps = {
    onelinkList,
    setOnelinkList,
    onelink,
    onelinkId: onelink?.onelinkId,
    setOnelink,
    replaceOnelink,
    loadOnelinks,
    linkCount,
    legacyCount,
    notLegacyCount,
  };

  const subscriptionProps: SubscriptionProps = {
    subscriptionSetup: account?.subscriptionSetup,
    paidUntil: account?.paidUntil,
    haveActiveSubscription: !!haveActiveSubscription,
    isSubscriptionOverDue: !!isSubscriptionOverDue,
    isSubscriptionGracePeriodOverDue: !!isSubscriptionGracePeriodOverDue,
    isCancelledAndOverFreeLimit: !!isCancelledAndOverFreeLimit,
    locked: !!isSubscriptionGracePeriodOverDue || !!isCancelledAndOverFreeLimit,
    currentPlanCancelled,
  };

  ///////////////////////////////////////////////////////////////////////////

  const setScreenViewPort = () => {
    if (window.location.pathname === Pages.MY_ONELINKS.path) {
      const viewport = document?.querySelector('meta[name="viewport"]');
      viewport?.setAttribute("content", "width=1000");
      setIsSmallScreen(false);
    } else {
      const viewport = document?.querySelector('meta[name="viewport"]');
      viewport?.setAttribute("content", "width=device-width, initial-scale=1");
      setIsSmallScreen(window.screen.width <= 720);
    }
  };

  ///////////////////////////////////////////////////////////////////////////

  useEffect(() => {
    const path = start_path;
    const token = start_token;

    setStartupPath(path);
    setStartupHref(start_href);

    setScreenViewPort();

    // GA CLIENT ID
    let gaId = ls.get(LS_GA_CLIENT_ID);
    if (!gaId) {
      gaId = uuidv4();
      if (cookies.analytics) ls.set(LS_GA_CLIENT_ID, gaId);
    }
    setGaClientId(gaId);

    // ATTRIBUTION
    const attribution: AccountAttribution = getAttribution(start_href, !!cookies.analytics);
    setAttribution(attribution);

    if (window.location.hostname !== "localhost")
      window.history.pushState("url-rewrite", document.title, path);

    const accessToken = retrieveAccessToken();

    switch (path) {
      case Pages.VERIFY_EMAIL.path:
        verifyEmail(token, loader, accessToken, defaultProps);
        break;
      case Pages.RESET_PASSWORD.path:
        setPasswordResetToken(token);
        return;
      // PAYMENT
      case Pages.PAYMENT_SUCCESS.path:
        //const sessionId = params.get("session_id");
        loader.setResult(
          true,
          _("payment", "head_setup_payment_success"),
          _("payment", "setup_payment_success")
        );
        navigate(Pages.MY_ONELINKS);
        break;
      case Pages.PAYMENT_CANCELLED.path:
        loader.setResult(
          true,
          _("payment", "head_setup_payment_failed"),
          _("payment", "setup_payment_failed")
        );
        break;
      case Pages.INVITATION.path:
        setInvitationToken(token);
        navigate(Pages.REGISTER);
        return;
      default:
        break;
    }

    const defaultOnSuccessNavigation = path === Pages.LANDING_PAGE.path;

    if (accessToken)
      signInWithAccessToken(
        accessToken,
        { ...defaultProps, ...authProps },
        defaultOnSuccessNavigation
      );

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    loadOnelinks();

    if (startupPath) {
      if (startupPath === Pages.PAYMENT_SUCCESS.path) {
        ReactGA.event("subscription_setup", {
          category: "subscription",
          action: "setup",
          label: account?.subscriptionSetup,
        });

        if (account?.subscriptionSetup && account?.currentPlan && account?.currentPlanPeriod) {
          const planPrice = PlanPrice[account?.currentPlan];
          const price = account.currentPlanPeriod === "monthly" ? planPrice.monthly : planPrice.yearly;

          const date = formatTimestamp(new Date().getTime() / 1000);
          const transaction_id = "T" + hashCode(account.uuid + account.subscriptionSetup + date);

          ReactGA.event("purchase", {
            transaction_id: transaction_id,
            value: price,
            currency: "USD",
            items: [
              { item_id: account.subscriptionSetup, item_name: account.subscriptionSetup },
            ]
          });
        }
      }

      navigate(startupPath);
      setStartupPath(undefined);
    }

    // eslint-disable-next-line
  }, [account]);

  useEffect(() => {
    if (gaClientId)
      ReactGA.send({ hitType: "pageview", page: window.location.pathname });
    setLastPath(path);
    setPath(window.location.pathname);

    if (
      account?.type === "pending" &&
      !PATHS_PENDING_ACCOUNT.has(window.location.pathname)
    ) {
      navigate(Pages.COMPLETE_REGISTRATION);
      return;
    }

    setScreenViewPort();

    // eslint-disable-next-line
  }, [_navigate]);

  const gaSendConsent = () => {
    const userSelection = (cookies?.analytics && "granted") || "denied";
    ReactGA._gtag("consent", "default", {
      ad_storage: userSelection,
      analytics_storage: userSelection,
      functionality_storage: userSelection,
      personalization_storage: userSelection,
      security_storage: userSelection,
    });
  };

  useEffect(() => {
    if (gaClientId) {
      const userSelection = (cookies?.analytics && "granted") || "denied";
      ReactGA.initialize([
        {
          trackingId: "G-4BBB0HXQSM",
          gtagOptions: {
            analytics_storage: userSelection,
            ad_storage: userSelection,
            client_id: gaClientId,
          }, // optional
        },
      ]);
      gaSendConsent();
    }
    // eslint-disable-next-line
  }, [gaClientId]);

  useEffect(() => {
    //KEEP setLocalTime(new Date().getTime() / 1000);
    // eslint-disable-next-line
  }, [serverTime]);
  ///////////////////////////////////////////////////////////////////////////

  const isLegal = [
    Pages.TERMS_OF_SERVICE.path,
    Pages.PRIVACY_POLICY.path,
    Pages.LEGAL_NOTICE.path,
  ].includes(window.location.pathname);

  const overrideRouteLocation = isLegal && lastPath;

  const errorBar = isSubscriptionOverDue || isCancelledAndOverFreeLimit;

  const faqItem = faqMap[path.substring(1)];

  if (BETA && !betaUnlocked)
    return <BetaWall setBetaUnlocked={setBetaUnlocked} />;

  const renderLazyLoader = () => <div className={css.lazyLoader}></div>;

  return (
    <div className={css.app}>
      <TopMenu
        {...defaultProps}
        {...authProps}
        {...onelinkProps}
        {...subscriptionProps}
        manageCookies={manageCookies}
      />
      <div
        className={
          errorBar ? classNames(css.content, css.errorbar) : css.content
        }
      >
        <Suspense fallback={renderLazyLoader()}>
          <Routes location={overrideRouteLocation || undefined}>
            <Route
              path={Pages.PRICING.path}
              element={<PricingPage {...defaultProps} />}
            />
            <Route
              path={Pages.ABOUT.path}
              element={<AboutPage {...defaultProps} />}
            />
            <Route
              path={Pages.SUPPORT.path}
              element={<SupportPage {...defaultProps} />}
            />
            {faqMap &&
              Object.keys(faqMap).map((id) => (
                <Route
                  key={id}
                  path={"/" + id}
                  element={
                    <SupportPage
                      {...defaultProps}
                      faqItem={faqItem}
                      faqTagMap={faqTagMap}
                    />
                  }
                />
              ))}
            <Route
              path={Pages.REGISTER.path}
              element={<RegisterWrapper {...defaultProps} {...authProps} />}
            />
            <Route
              path={Pages.SIGNIN.path}
              element={<SignInWrapper {...defaultProps} {...authProps} />}
            />
            <Route
              path={Pages.RESET_PASSWORD.path}
              element={<ResetPassword {...defaultProps} {...authProps} />}
            />
            {user && account && (
              <>
                <Route
                  path={Pages.MY_ONELINKS.path}
                  element={
                    <MyOnelinks
                      {...defaultProps}
                      {...onelinkProps}
                      {...subscriptionProps}
                    />
                  }
                />
                <Route
                  path={Pages.COMPLETE_REGISTRATION.path}
                  element={<CompleteRegistration {...defaultProps} />}
                />
                <Route
                  path={Pages.ACCOUNT.path}
                  element={
                    <MyAccount
                      {...defaultProps}
                      {...onelinkProps}
                      {...subscriptionProps}
                    />
                  }
                />
                <Route
                  path={Pages.MANAGE_ACCESS.path}
                  element={<ManageAccess {...defaultProps} />}
                />
                <Route
                  path={Pages.SETUP_PAYMENT.path}
                  element={
                    <SetupPaymentWrapper {...defaultProps} {...onelinkProps} />
                  }
                />
                <Route
                  path={Pages.USER_SETTINGS.path}
                  element={<UserSettings {...defaultProps} />}
                />
              </>
            )}
            <Route
              path="*"
              element={<LandingPage {...defaultProps} inBackground={isLegal} />}
            />
          </Routes>
        </Suspense>
        {!cookies?.date && <div className={css.cookiePadding} />}
      </div>
      <div className={css.footer}>
        <CookieBanner cookies={cookies} setCookies={setCookies} />
        <Footer {...defaultProps} />
      </div>
      {loader.show && <Loader {...loaderState}></Loader>}

      <Suspense fallback={renderLazyLoader()}>
        <Routes>
          <Route
            path={Pages.TERMS_OF_SERVICE.path}
            element={
              <Legal
                open={true}
                type="tos"
                navigate={navigate}
                closeTo={{ path: lastPath, title: lastTitle }}
              />
            }
          />
          <Route
            path={Pages.PRIVACY_POLICY.path}
            element={
              <Legal
                open={true}
                type="privacy"
                navigate={navigate}
                closeTo={{ path: lastPath, title: lastTitle }}
              />
            }
          />
          <Route
            path={Pages.LEGAL_NOTICE.path}
            element={
              <Legal
                open={true}
                type="notice"
                navigate={navigate}
                closeTo={{ path: lastPath, title: lastTitle }}
              />
            }
          />
        </Routes>
      </Suspense>
    </div>
  );
};

export default App;
